// A segment or a triggered email targets either a brand - reaching every Ongage
// list under it - or a single list configured on its own. One dropdown offers
// both, so option values carry which section they came from: brand IDs and list
// IDs are separate number spaces and are free to overlap.
const BRAND_PREFIX = "brand:";
const LIST_PREFIX = "list:";

/**
 * Look a brand up by its Brand ID, or a list by its List ID. A saved automation
 * can carry either as a number or a string, so both are compared in string form.
 */
export const findBrandById = (brandId: any, brands: any[] = []) =>
  brands.find((brand) => String(brand.id) === String(brandId));

export const findListById = (listId: any, lists: any[] = []) =>
  lists.find((list) => String(list.list_id) === String(listId));

/**
 * The grouped options a target dropdown offers. The transactional group is left
 * out entirely when the user is not entitled to any list, rather than shown
 * empty.
 */
export const targetOptions = (config: any) => {
  const brands: any[] = config?.BRANDS ?? [];
  const lists: any[] = config?.LISTS ?? [];

  const groups = [
    {
      label: "Brands",
      options: brands.map((brand) => ({
        label: brand.name,
        value: `${BRAND_PREFIX}${brand.id}`,
      })),
    },
  ];

  if (lists.length) {
    groups.push({
      label: "Transactional lists",
      options: lists.map((list) => ({
        label: list.name,
        value: `${LIST_PREFIX}${list.list_id}`,
      })),
    });
  }

  return groups;
};

/**
 * The dropdown value for an automation's saved target. A list wins over a brand
 * because a list-bound segment stores both.
 */
export const isListTarget = (params: any) =>
  params?.list_id !== undefined && params?.list_id !== null;

export const targetValue = (params: any) => {
  if (isListTarget(params)) {
    return `${LIST_PREFIX}${params.list_id}`;
  }
  if (params?.brand !== undefined && params?.brand !== null) {
    return `${BRAND_PREFIX}${params.brand}`;
  }
  return undefined;
};

/**
 * The params to save when a target is chosen. Both IDs are returned together so
 * that they can be written in a single state update, and they are read back out
 * of the configuration so they keep the types it holds them in.
 *
 * Choosing a brand clears any list the automation had, which is what returns it
 * to targeting every list under that brand.
 */
export const targetParams = (value: any, config: any, params: any = {}) => {
  const selected = String(value ?? "");

  if (selected.startsWith(LIST_PREFIX)) {
    const list = findListById(selected.slice(LIST_PREFIX.length), config?.LISTS);

    return { ...params, brand: list?.brand_id, list_id: list?.list_id };
  }

  const brand = findBrandById(selected.slice(BRAND_PREFIX.length), config?.BRANDS);

  return { ...params, brand: brand?.id, list_id: undefined };
};

/**
 * Whether a dropdown option stands for a single list rather than a brand.
 */
export const isListOption = (value: any) => String(value ?? "").startsWith(LIST_PREFIX);

/**
 * What choosing this target will do, spelled out under the dropdown. Brands and
 * lists look alike once chosen, and the difference only shows up later in
 * Ongage, so the form says it outright.
 */
export const targetHint = (type: string, params: any) => {
  if (!targetValue(params)) {
    return undefined;
  }

  if (isListTarget(params)) {
    return type === "email" ? "Sends to this list only." : "Populates only this list.";
  }

  return type === "email"
    ? "Sends to the brand's own list."
    : "Populates every list under this brand.";
};

/**
 * What an automation does, worded the way the form words it. Falls back to the
 * raw type, so a trigger type the configuration no longer lists still reads as
 * something.
 */
export const targetTypeLabel = (type: string, config: any) =>
  (config?.TARGETS ?? []).find((target: any) => target.type === type)?.label ?? type ?? "";

/**
 * The target an automation points at, for display. A target that no longer
 * matches anything in the configuration falls back to its raw ID, so a list or
 * brand that has since been removed shows as a number rather than a blank.
 */
export const targetLabel = (event: any, config: any) => {
  const params = event?.params ?? {};

  if (event?.type === "suppression") {
    return { name: params.list_name ?? "", isList: false };
  }

  if (isListTarget(params)) {
    const list = findListById(params.list_id, config?.LISTS);

    return { name: list?.name ?? String(params.list_id), isList: true };
  }

  const brand = findBrandById(params.brand, config?.BRANDS);

  return {
    name: brand?.name ?? (params.brand === undefined ? "" : String(params.brand)),
    isList: false,
  };
};
